import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';

const buildCommandMap = (commands) => {
  const map = {};
  (commands || []).forEach((cmd) => {
    if (!cmd || !cmd.command) return;
    map[cmd.command] = cmd;
  });
  return map;
};

const isCommandAvailable = (available, command) => {
  if (!available) return true;
  if (available instanceof Set) return available.has(command);
  if (Array.isArray(available)) return available.includes(command);
  if (typeof available === 'object') return Boolean(available[command]);
  return true;
};

export default function UnifiedTvControl({ commands = [], onCommand, onInputEnd, available, hdmiSources = null, tvHomeSource = false, powerControls = null, moreControls = null, forceSourceAvailability = true, adbPowerCandidates = false, sourceCandidates = false, repeatLimit = null }) {
  const commandMap = useMemo(() => buildCommandMap(commands), [commands]);
  const [showNumberPad, setShowNumberPad] = useState(false);
  const [moreControlsOpened, setMoreControlsOpened] = useState(false);
  const [showPowerControls, setShowPowerControls] = useState(false);
  const visibleHdmiSources = Array.isArray(hdmiSources)
    ? hdmiSources
    : [1, 2, 3, 4].map((port) => ({ port }));

  const handleCommand = useCallback(
    (command, input) => {
      if (!onCommand) return;
      if (input) onCommand(command, input);
      else onCommand(command);
    },
    [onCommand]
  );

  const renderButton = (commandKey, props = {}) => {
    const cmd = commandMap[commandKey];
    if (!cmd) return null;
    const { forceEnabled = false, ...buttonProps } = props;
    const enabled = forceEnabled || isCommandAvailable(available, commandKey);
    return (
      <Btn
        key={commandKey}
        label={cmd.label}
        icon={cmd.icon}
        command={commandKey}
        onCommand={handleCommand}
        onInputEnd={onInputEnd}
        repeatLimit={repeatLimit}
        disabled={!enabled}
        {...buttonProps}
      />
    );
  };

  const renderOk = (commandKey) => {
    const cmd = commandMap[commandKey];
    if (!cmd) return null;
    const enabled = isCommandAvailable(available, commandKey);
    const circleCls =
      'w-20 h-20 sm:w-24 sm:h-24 rounded-full grid place-items-center font-bold text-sm border border-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06),0_10px_20px_rgba(0,0,0,0.5)]';
    const circleStyle = {
      background: 'radial-gradient(100px 100px at 50% 45%, #203046 0%, #1b2739 60%, #172232 100%)',
    };

    return (
      <button
        type="button"
        aria-label={cmd.label}
        className={`${circleCls} ${enabled ? '' : 'invisible pointer-events-none'}`}
        style={circleStyle}
        onPointerDown={(event) => {
          event.preventDefault();
          if (!enabled) return;
          handleCommand(commandKey);
        }}
      >
        {cmd.icon || cmd.label}
      </button>
    );
  };

  return (
    <main
      role="application"
      aria-label="Unified TV Controls"
      className="w-full max-w-none min-w-0 rounded-[30px] border border-white/10 shadow-2xl p-3 sm:p-4 lg:p-5 grid gap-3 sm:gap-4 text-slate-100"
      style={{
        background: 'linear-gradient(180deg, #121821 0%, #0f1724 58%, #0d141d 100%)',
      }}
    >
      {/* Top bar */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2 sm:gap-3">
        {renderButton('power', { variant: 'primary' })}
        {renderButton('input')}
        {renderButton('settings')}
      </div>

      {/* Navigation cluster */}
      <section className="grid grid-cols-[minmax(72px,0.95fr)_minmax(0,1.8fr)] items-start gap-3 sm:gap-4">
        {/* Volume */}
        <div>
          <div className="text-center text-[11px] sm:text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Volume</div>
          <div className="grid gap-2">
            {renderButton('vol_up', { pill: true, repeat: true, compact: true })}
            {renderButton('vol_down', { pill: true, repeat: true, compact: true })}
            {renderButton('mute', { compact: true })}
          </div>
        </div>

        {/* D-Pad */}
        <div className="grid grid-cols-3 grid-rows-3 gap-2 place-items-center self-center" aria-label="Directional pad">
          <div />
          {renderButton('nav_up', { round: true, nav: true, repeat: Boolean(onInputEnd) })}
          <div />
          {renderButton('nav_left', { round: true, nav: true, repeat: Boolean(onInputEnd) })}
          {renderOk('ok')}
          {renderButton('nav_right', { round: true, nav: true, repeat: Boolean(onInputEnd) })}
          <div />
          {renderButton('nav_down', { round: true, nav: true, repeat: Boolean(onInputEnd) })}
          <div />
        </div>
      </section>

      {/* System keys */}
      <div className="grid grid-cols-2 gap-6 sm:gap-10">
        {renderButton('back')}
        {renderButton('home')}
      </div>

      {/* Direct or trained HDMI source keys */}
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {tvHomeSource ? renderButton('tv_home', { subtitle: 'TV itself', sourceButton: true, forceEnabled: forceSourceAvailability }) : null}
        {visibleHdmiSources.map((source) => sourceCandidates && source.port > 4 ? (
          <button key={source.port} type="button" disabled className="opacity-40" aria-label={`HDMI ${source.port}`}>
            HDMI {source.port} · Direct key unavailable
          </button>
        ) : renderButton(`hdmi_${source.port}`, {
          subtitle: source.playerName || '',
          sourceButton: true,
          hideWhenUnavailable: !sourceCandidates,
          forceEnabled: forceSourceAvailability && Array.isArray(hdmiSources),
        }))}
      </div>

      {/* More controls */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-2 sm:p-3">
        <button
          type="button"
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3 text-left text-sm font-semibold tracking-tight text-slate-100 transition hover:bg-white/[0.07]"
          onClick={() => { setMoreControlsOpened(true); setShowNumberPad((prev) => !prev); }}
          aria-expanded={showNumberPad}
        >
          <span>More Controls</span>
          <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
            {showNumberPad ? 'Hide' : 'Show'}
          </span>
        </button>
        {/* Retain the administrative request/key when More Controls is hidden. */}
        <div hidden={!showNumberPad}>{moreControlsOpened ? moreControls : null}</div>
        {showNumberPad && (
          <div className="grid gap-3 mt-3">
            {adbPowerCandidates ? (
              <div>
                <div className="text-center text-[11px] sm:text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Power</div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {renderButton('wake', { hideWhenUnavailable: false })}
                  {renderButton('sleep', { hideWhenUnavailable: false })}
                </div>
              </div>
            ) : null}
            <div>
              <div className="text-center text-[11px] sm:text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Channel</div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {renderButton('chan_up', { pill: true, repeat: true, hideWhenUnavailable: false })}
                {renderButton('chan_down', { pill: true, repeat: true, hideWhenUnavailable: false })}
                {renderButton('last')}
              </div>
            </div>
            <div>
              <div className="text-center text-[11px] sm:text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Playback</div>
              <div className="grid grid-cols-4 gap-2 sm:gap-3">
                {renderButton('rew')}
                {renderButton('play_pause')}
                {renderButton('ff')}
                {renderButton('stop')}
              </div>
            </div>
            <div>
              <div className="text-center text-[11px] sm:text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Number Pad</div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {[
                  'num_1',
                  'num_2',
                  'num_3',
                  'num_4',
                  'num_5',
                  'num_6',
                  'num_7',
                  'num_8',
                  'num_9',
                  'dash',
                  'num_0',
                  'enter',
                ].map((commandKey) => renderButton(commandKey))}
              </div>
            </div>
          </div>
        )}
      </section>

      {powerControls ? (
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-2 sm:p-3">
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3 text-left text-sm font-semibold tracking-tight text-slate-100 transition hover:bg-white/[0.07]"
            onClick={() => setShowPowerControls((prev) => !prev)}
            aria-expanded={showPowerControls}
          >
            <span>Power Controls</span>
            <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
              {showPowerControls ? 'Hide' : 'Show'}
            </span>
          </button>
          {showPowerControls ? <div className="grid gap-3 mt-3">{powerControls}</div> : null}
        </section>
      ) : null}

      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* Color keys */}
      <div className="grid grid-cols-4 gap-2 sm:gap-3 select-none">
        <ColorKey
          commandKey="color_red"
          color="#d32f2f"
          commandMap={commandMap}
          available={available}
          onCommand={handleCommand}
        />
        <ColorKey
          commandKey="color_green"
          color="#2e7d32"
          commandMap={commandMap}
          available={available}
          onCommand={handleCommand}
        />
        <ColorKey
          commandKey="color_yellow"
          color="#f9a825"
          commandMap={commandMap}
          available={available}
          onCommand={handleCommand}
        />
        <ColorKey
          commandKey="color_blue"
          color="#1565c0"
          commandMap={commandMap}
          available={available}
          onCommand={handleCommand}
        />
      </div>
    </main>
  );
}

function Btn({
  label,
  icon,
  command,
  onCommand,
  onInputEnd,
  round,
  pill,
  compact,
  nav,
  repeat,
  repeatLimit,
  variant,
  disabled,
  hideWhenUnavailable = true,
  subtitle,
  sourceButton,
}) {
  const downRef = useRef(null);
  const repRef = useRef(null);
  const holdRef = useRef(null);

  const stopPress = useCallback(() => {
    if (downRef.current) window.clearTimeout(downRef.current);
    if (repRef.current) window.clearInterval(repRef.current);
    downRef.current = null;
    repRef.current = null;
    const holdId = holdRef.current;
    holdRef.current = null;
    if (holdId && onInputEnd) onInputEnd(holdId);
  }, [onInputEnd]);

  useEffect(() => {
    const hide = () => { if (document.hidden) stopPress(); };
    window.addEventListener('blur', stopPress);
    window.addEventListener('offline', stopPress);
    window.addEventListener('pagehide', stopPress);
    document.addEventListener('visibilitychange', hide);
    return () => {
      stopPress();
      window.removeEventListener('blur', stopPress);
      window.removeEventListener('offline', stopPress);
      window.removeEventListener('pagehide', stopPress);
      document.removeEventListener('visibilitychange', hide);
    };
  }, [stopPress, command, disabled]);

  const cls = useMemo(() => {
    const base =
      'appearance-none select-none cursor-pointer rounded-xl font-semibold tracking-tight flex items-center justify-center gap-2 transition-transform active:translate-y-[1px] border border-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05),0_6px_18px_rgba(0,0,0,0.35)] focus:outline-none focus:ring-2 focus:ring-cyan-400/40 text-center leading-tight';
    const sizing = compact
      ? 'px-2.5 py-2 min-h-[38px] text-[0.72rem] md:px-3.5 md:py-3 md:min-h-[56px] md:text-[0.92rem] lg:px-2.5 lg:py-2 lg:min-h-[45px] lg:text-[0.78rem]'
      : 'px-2.5 py-2.5 min-h-[48px] text-[0.8rem] md:px-3.5 md:py-3 md:min-h-[56px] md:text-[0.92rem] lg:px-2.5 lg:py-2.5 lg:min-h-[50px] lg:text-[0.82rem]';
    const shape = round
      ? nav
        ? 'rounded-full w-10 h-10 p-0 text-sm md:w-14 md:h-14 md:text-lg lg:w-[50px] lg:h-[50px] lg:text-base'
        : 'rounded-full w-11 h-11 p-0 text-base md:w-14 md:h-14 md:text-lg lg:w-[52px] lg:h-[52px] lg:text-base'
      : pill
        ? 'rounded-full'
        : '';
    const bgPrimary = 'bg-gradient-to-b from-[#ff686b] to-[#ab1f21] hover:brightness-105';
    const bgDefault = 'bg-gradient-to-b from-[#1b2533] to-[#1a2739] hover:from-[#223043]';
    const hidden = disabled
      ? hideWhenUnavailable ? 'invisible pointer-events-none' : 'opacity-40 cursor-not-allowed'
      : '';
    return [base, sizing, shape, variant === 'primary' ? bgPrimary : bgDefault, hidden].join(' ');
  }, [compact, nav, round, pill, variant, disabled, hideWhenUnavailable]);

  const pressStart = useCallback(
    (event) => {
      event.preventDefault();
      if (disabled) return;
      if (!onCommand) return;
      if (event.button > 0 || holdRef.current) return;
      const holdId = {};
      holdRef.current = holdId;
      if (Number.isInteger(event.pointerId)) event.currentTarget.setPointerCapture?.(event.pointerId);
      if (repeat && onInputEnd) onCommand(command, { holdId, repeat: false });
      else onCommand(command);
      let taps = 1;
      if (repeat) {
        downRef.current = window.setTimeout(() => {
          repRef.current = window.setInterval(() => {
            if (repeatLimit && taps >= repeatLimit) { stopPress(); return; }
            taps += 1;
            if (onInputEnd) onCommand(command, { holdId, repeat: true });
            else onCommand(command);
          }, 120);
        }, 450);
      }
    },
    [command, onCommand, onInputEnd, repeat, repeatLimit, disabled, stopPress]
  );

  const pressEnd = useCallback(
    (event) => {
      event.preventDefault();
      stopPress();
    },
    [stopPress]
  );

  return (
    <button
      type="button"
      className={cls}
      aria-label={label}
      disabled={disabled}
      onPointerDown={pressStart}
      onPointerUp={pressEnd}
      onPointerLeave={pressEnd}
      onPointerCancel={pressEnd}
      onLostPointerCapture={pressEnd}
      onClick={(event) => event.preventDefault()}
    >
      {sourceButton ? (
        <span className="flex min-w-0 flex-col items-center justify-center leading-none">
          <span className="text-[0.68rem] sm:text-[0.74rem] font-semibold">{icon || label}</span>
          {subtitle ? (
            <span className="mt-1 max-w-full truncate text-[0.58rem] sm:text-[0.64rem] font-semibold text-cyan-300" title={subtitle}>
              {subtitle}
            </span>
          ) : null}
        </span>
      ) : icon || label}
    </button>
  );
}

function ColorKey({ color, commandKey, commandMap, available, onCommand }) {
  const cmd = commandMap[commandKey];
  if (!cmd) return null;
  const enabled = isCommandAvailable(available, commandKey);
  return (
    <button
      type="button"
      className={`h-12 sm:h-14 rounded-xl border border-white/15 ${enabled ? '' : 'invisible pointer-events-none'}`}
      style={{ background: color }}
      aria-label={cmd.label}
      onPointerDown={(event) => {
        event.preventDefault();
        if (!enabled) return;
        onCommand(commandKey);
      }}
      onClick={(event) => event.preventDefault()}
    >{cmd.label}</button>
  );
}
